import React from "react"
import ReactTooltip from "react-tooltip"
import { useTranslation } from "react-i18next"

import * as DocumentApi from "services/api/DocumentApi"
import { useAlert } from "hooks"

import { Spinner, TrashIcon, Button, variants, sizes } from "components/Elements"

import { ErrorResponse, UploadedDocument } from "models"

const buttonDeleteStatusEnum = {
	loading: "loading",
	ready: "ready",
}

type ButtonDeleteFileProps = {
	file: UploadedDocument
	label?: string
	className?: string
	variant?: keyof typeof variants
	size?: keyof typeof sizes
	disabled?: boolean
	onFileDelete?: () => void
}

export const ButtonDeleteFile: React.FC<ButtonDeleteFileProps> = ({
	file,
	className = "",
	variant,
	size,
	disabled,
	onFileDelete,
}) => {
	const { t } = useTranslation()
	const { alert } = useAlert()

	const [buttonDeleteStatus, setbuttonDeleteStatus] = React.useState<
		typeof buttonDeleteStatusEnum[keyof typeof buttonDeleteStatusEnum]
	>(buttonDeleteStatusEnum.ready)
	const isLoading = buttonDeleteStatus === buttonDeleteStatusEnum.loading

	const handleDocumentDelete = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		try {
			setbuttonDeleteStatus(buttonDeleteStatusEnum.loading)
			const { getError } = await DocumentApi.deleteDocument(file.document_id)
			if (getError()) throw getError()
			if (onFileDelete) onFileDelete()
			alert(
				"success",
				t("file_delete_success", {
					deletedFile: file.document_data.custom_name,
					ns: "form",
				})
			)
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
			setbuttonDeleteStatus(buttonDeleteStatusEnum.ready)
		}
	}

	return (
		<>
			{!isLoading && (
				<>
					<Button
						variant={variant}
						size={size}
						onClick={handleDocumentDelete}
						className={`c-input-file__button ${className}`}
						disabled={disabled}
						additionalProps={{
							"aria-label": t("file_delete_file", { ns: "form" }),
							"data-tip": t("file_delete_file", { ns: "form" }),
						}}
					>
						<TrashIcon size="15" />
					</Button>
					<ReactTooltip backgroundColor={"var(--primaryColor)"} />
				</>
			)}

			{isLoading && <Spinner color={"var(--secondaryColor"} size={"20px"} />}
		</>
	)
}
